import NetworkService from "../config/interceptor/interceptor";

export interface PromoCodeDTO {
  id: string;
  code: string;
  discountPercentage: number;
  coinsReward: number;
  usageCount: number;
  isActive: boolean;
  isSpecialReward?: boolean;
  ownerId: string;
  ownerName: string;
  ownerRole: string;
  ownerCoins?: number;
  createdAt: string;
  updatedAt: string;
}

export interface PromoCodeValidationResponse {
  isValid: boolean;
  message: string;
  discountPercentage?: number;
  promoCode?: string;
  isSpecialReward?: boolean;
}

export interface PromoCodeSettings {
  id: number;
  defaultDiscountPercentage: number;
  defaultCoinsReward: number;
}

export interface TeacherMonthlyPointsDTO {
  id: string;
  teacherId: string;
  teacherName: string;
  year: number;
  month: number;
  points: number;
  amountTnd: number;
  createdAt: string;
  updatedAt: string;
}

const unwrap = <T>(response: any): T => response?.data?.data ?? response?.data;

/**
 * Generate or get user's personal promo code
 */
export const generatePromoCode = async (userId: string): Promise<PromoCodeDTO> => {
  const response = await NetworkService.getInstance().sendHttpRequest({
    url: "promo-codes/generate",
    method: "POST",
    withLoader: true,
    withFailureLogs: true,
  });
  return unwrap<PromoCodeDTO>(response);
};

/**
 * Get user's personal promo code (auto-generates if doesn't exist)
 */
export const getMyPromoCode = async (userId: string): Promise<PromoCodeDTO> => {
  const response = await NetworkService.getInstance().sendHttpRequest({
    url: "promo-codes/my-code",
    method: "GET",
    withLoader: false,
    withFailureLogs: true,
  });
  return unwrap<PromoCodeDTO>(response);
};

/**
 * Validate a promo code
 */
export const validatePromoCode = async (
  code: string,
  userId: string,
  offerId?: string,
  selectedPeriod?: string
): Promise<PromoCodeValidationResponse> => {
  const response = await NetworkService.getInstance().sendHttpRequest({
    url: "promo-codes/validate",
    method: "POST",
    params: { code, offerId, selectedPeriod },
    withLoader: false,
    withFailureLogs: false,
  });
  return unwrap<PromoCodeValidationResponse>(response);
};

/**
 * Apply a promo code
 */
export const applyPromoCode = async (
  code: string,
  userId: string,
  selectedPeriod?: string
): Promise<string> => {
  const response = await NetworkService.getInstance().sendHttpRequest({
    url: "promo-codes/apply",
    method: "POST",
    params: { code, selectedPeriod },
    withLoader: true,
    withFailureLogs: true,
  });
  return unwrap<string>(response);
};

/**
 * Get all promo codes (admin only)
 */
export const getAllPromoCodes = async (): Promise<PromoCodeDTO[]> => {
  const response = await NetworkService.getInstance().sendHttpRequest({
    url: "promo-codes/all",
    method: "GET",
    withLoader: true,
    withFailureLogs: true,
  });
  return unwrap<PromoCodeDTO[]>(response);
};

/**
 * Update promo code discount (admin only)
 */
export const updatePromoCodeDiscount = async (
  promoCodeId: string,
  discount: number
): Promise<PromoCodeDTO> => {
  const response = await NetworkService.getInstance().sendHttpRequest({
    url: `promo-codes/${promoCodeId}/discount`,
    method: "PUT",
    params: { discount },
    withLoader: true,
    withFailureLogs: true,
  });
  return unwrap<PromoCodeDTO>(response);
};

/**
 * Update promo code reward/coins value (admin only)
 */
export const updatePromoCodeReward = async (
  promoCodeId: string,
  reward: number
): Promise<PromoCodeDTO> => {
  const response = await NetworkService.getInstance().sendHttpRequest({
    url: `promo-codes/${promoCodeId}/reward`,
    method: "PUT",
    params: { reward },
    withLoader: true,
    withFailureLogs: true,
  });
  return unwrap<PromoCodeDTO>(response);
};

/**
 * Activate or deactivate a promo code (admin only)
 */
export const updatePromoCodeStatus = async (
  promoCodeId: string,
  active: boolean
): Promise<PromoCodeDTO> => {
  const response = await NetworkService.getInstance().sendHttpRequest({
    url: `promo-codes/${promoCodeId}/status`,
    method: "PUT",
    params: { active },
    withLoader: true,
    withFailureLogs: true,
  });
  return unwrap<PromoCodeDTO>(response);
};

/**
 * Bulk update promo codes by owner role (admin only)
 */
export const updatePromoCodesByRole = async (
  roleName: string,
  discount?: number,
  reward?: number,
  active?: boolean
): Promise<PromoCodeDTO[]> => {
  const params: Record<string, any> = { roleName };
  if (discount !== undefined) params.discount = discount;
  if (reward !== undefined) params.reward = reward;
  if (active !== undefined) params.active = active;

  const response = await NetworkService.getInstance().sendHttpRequest({
    url: "promo-codes/bulk-by-role",
    method: "PUT",
    params,
    withLoader: true,
    withFailureLogs: true,
  });
  return unwrap<PromoCodeDTO[]>(response);
};

/**
 * Reset rewards for a user (admin only)
 * Optionally clears teacher points when resetTeacherPoints = true
 */
export const resetUserRewards = async (
  userId: string,
  resetTeacherPoints = false
): Promise<string> => {
  const response = await NetworkService.getInstance().sendHttpRequest({
    url: "promo-codes/reset-rewards",
    method: "PUT",
    params: { userId, resetTeacherPoints },
    withLoader: true,
    withFailureLogs: true,
  });
  return unwrap<string>(response);
};

/**
 * Get promo code settings (admin only)
 */
export const getPromoCodeSettings = async (): Promise<PromoCodeSettings> => {
  const response = await NetworkService.getInstance().sendHttpRequest({
    url: "promo-codes/settings",
    method: "GET",
    withLoader: false,
    withFailureLogs: true,
  });
  return unwrap<PromoCodeSettings>(response);
};

/**
 * Update promo code settings (admin only)
 */
export const updatePromoCodeSettings = async (
  defaultDiscount: number,
  defaultCoins: number
): Promise<PromoCodeSettings> => {
  const response = await NetworkService.getInstance().sendHttpRequest({
    url: "promo-codes/settings",
    method: "PUT",
    params: { defaultDiscount, defaultCoins },
    withLoader: true,
    withFailureLogs: true,
  });
  return unwrap<PromoCodeSettings>(response);
};

/**
 * Redeem NJE7EKCOINS for an offer (once per year)
 */
export const redeemCoins = async (
  userId: string,
  coinsToRedeem: number
): Promise<string> => {
  const response = await NetworkService.getInstance().sendHttpRequest({
    url: "promo-codes/redeem-coins",
    method: "POST",
    params: { coinsToRedeem },
    withLoader: true,
    withFailureLogs: true,
  });
  return unwrap<string>(response);
};

/**
 * Check if user can redeem coins this year
 */
export const canRedeemCoins = async (userId: string): Promise<boolean> => {
  const response = await NetworkService.getInstance().sendHttpRequest({
    url: "promo-codes/can-redeem",
    method: "GET",
    withLoader: false,
    withFailureLogs: true,
  });
  return unwrap<boolean>(response);
};

/**
 * Get teacher's monthly points history
 */
export const getTeacherMonthlyPoints = async (
  teacherId: string
): Promise<TeacherMonthlyPointsDTO[]> => {
  const response = await NetworkService.getInstance().sendHttpRequest({
    url: "promo-codes/teacher-monthly-points",
    method: "GET",
    params: { teacherId },
    withLoader: true,
    withFailureLogs: true,
  });
  return unwrap<TeacherMonthlyPointsDTO[]>(response);
};

/**
 * Generate a special promo code (requires 50 NJE7EKCOINS)
 */
export const generateSpecialPromoCode = async (userId: string): Promise<PromoCodeDTO> => {
  const response = await NetworkService.getInstance().sendHttpRequest({
    url: "promo-codes/special",
    method: "POST",
    withLoader: true,
    withFailureLogs: true,
  });
  return unwrap<PromoCodeDTO>(response);
};

/**
 * Get the current user's active special promo code (if any)
 */
export const getSpecialPromoCode = async (userId: string): Promise<PromoCodeDTO | null> => {
  const response = await NetworkService.getInstance().sendHttpRequest({
    url: "promo-codes/special",
    method: "GET",
    withLoader: false,
    withFailureLogs: true,
  });
  return unwrap<PromoCodeDTO | null>(response);
};
